<template>
  <div>
    <div class="d-flex justify-content-end">
      <div class="d-flex justify-content-between button-group">
        <img src="@/assets/images/common-icons/icon_clear.png" @click="cleanFilters"
             :title="$t('buttons.common.clear_filters')">
        <img src="@/assets/images/common-icons/icon_search.svg" @click="search"
             :title="$t('buttons.common.search')">
      </div>
    </div>
    <div class="search-form">
      <div class="search-form__filters">
        <b-row>
          <b-col>
            <label>{{$t('labels.common.type')}}</label>
            <multiselect v-model="searchData.related_instance_type" :max-height="200"
                         placeholder="" :multiple="true" :close-on-select="false"
                         label="key" track-by="value" :options="instances_types"
                         select-label="" deselect-label="" selected-label=""
            ></multiselect>
          </b-col>
          <b-col>
            <label>{{$t('labels.common.instance_name')}}</label>
            <input class="form-control" placeholder="" v-model="searchData.related_instance_name">
          </b-col>
          <b-col>
            <label>{{$t('labels.common.document_name')}}</label>
            <input class="form-control" placeholder="" v-model="searchData.filename">
          </b-col>
          <b-col>
            <label>{{$t('labels.common.uploader')}}</label>
            <multiselect v-model="searchData.uploaded_by" :max-height="200"
                         placeholder="" label="username" track-by="username"
                         :options="users" :multiple="false"
                         select-label="" deselect-label="" selected-label=""
                         :close-on-select="true"></multiselect>
          </b-col>
          <b-col>
            <label>{{$t('labels.common.upload_at')}}</label>
            <datepicker v-model="searchData.uploadAt" range range-separator="/"
                        placeholder=""
                        lang="en" :first-day-of-week="1"></datepicker>
          </b-col>
        </b-row>
      </div>
    </div>
    <div>
      <b-row>
        <b-col cols="4">
          <input v-model="searchData.quick_search" class="form-control"
                 placeholder=""
                 @input="setQuickFilter">
        </b-col>
        <b-col class="d-flex justify-content-end">
          <b-btn variant="outline-warning"
                 :disabled="selectedAttachmentsCount === 0 || selectedAttachmentsCount > 10"
                 :title="selectedAttachmentsCount === 0 || selectedAttachmentsCount > 10
                  ? 'Select great then 0 & less then 10 attachments' : 'Download selected'"
                 @click="downloadAttachment">
            {{$t('buttons.common.download_selected')}}
          </b-btn>
        </b-col>
      </b-row>
    </div>
    <div>
      <b-row>
        <b-col>
          <ag-grid-vue class="ag-theme-balham" :gridOptions="attachmentsStorageGridOptions"
                       :rowData="attachmentsStorage" :columnDefs="attachmentsStorageColumns"
                       :style="gridHeight" @selectionChanged="countSelectedAttachments"
                       @rowDoubleClicked="downloadAttachment"
                       :postProcessPopup="postProcessPopup">
          </ag-grid-vue>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
  import {mapState} from 'vuex';
  import {AgGridVue} from 'ag-grid-vue';
  import Datepicker from 'vue2-datepicker';
  import Multiselect from 'vue-multiselect';

  import {
    formatNumber,
    autoResize,
    alignCells,
    notificationsRenderer
  } from '@/helpers/renderHelpers';

  import {gridMixins} from '@/mixins';

  import {
    addColIdToColumns,
    checkSavedFilterData,
    cleanFilters,
    getArrayByProperty,
    getFileNameFromContentDisposition,
    getFilteredSelectedData, getFilteredSelectedNodes,
    handlerResponse,
    setFilters, setNotifyData,
    transformDateFromDateRangeToStringFormatByIndex
  } from '@/helpers/commonHelpers';

  import gridProps from '@/gridProps';

  export default {
    mixins: [gridMixins],
    components: {
      AgGridVue,
      Datepicker,
      Multiselect,
      'ag-grid-vue': AgGridVue
    },
    data() {
      return {
        searchData: {
          related_instance_type: [],
          related_instance_name: '',
          filename: '',
          uploadAt: [],
          uploaded_by: null,
          quick_search: ''
        },
        storageKey: 'documentsStorage',
        gridOptions: {
          rowSelection: 'multiple',
          defaultColDef: {
            sortable: true,
            filter: true,
            resizable: true,
            enableValue: true,
            enableRowGroup: true,
            valueFormatter(params) {
              return formatNumber(params.value);
            },
            cellStyle(params) {
              return alignCells(params);
            }
          },
          onRowDataChanged() {
            if (this.rowData && this.rowData.length) {
              autoResize(this);
            } else {
              this.api && this.api.hideOverlay();
              this.api && this.api.showNoRowsOverlay();
            }
          },
          ...gridProps
        },
        attachmentsStorage: [],
        attachmentsStorageColumns: [],
        attachmentsStorageGridName: '',
        selectedAttachmentsCount: 0
      }
    },
    computed: {
      ...mapState('generic', ['instances_types']),
      ...mapState('woodware', ['users']),
      attachmentsStorageGridOptions() {
        return {
          ..._.cloneDeep(this.gridOptions),
          defaultColDef: {
            sortable: true,
            filter: true,
            resizable: true,
            enableValue: true,
            enableRowGroup: true,
            cellStyle(params) {
              return alignCells(params);
            },
          }
        }
      },
      gridHeight() {
        return {height: 'calc(100vh - 215px)'};
      },
    },
    watch: {
      'searchData.quick_search': {
        deep: true,
        handler () {
          this.setQuickFilter()
        }
      }
    },
    methods: {
      countSelectedAttachments() {
        this.selectedAttachmentsCount = getFilteredSelectedNodes(this.attachmentsStorageGridOptions).length;
      },
      prepareSearchData() {
        return {
          related_instance_type: getArrayByProperty(this.searchData.related_instance_type, 'value').toString(),
          related_instance_name: this.searchData.related_instance_name,
          filename: this.searchData.filename,
          uploaded_by: this.searchData.uploaded_by && this.searchData.uploaded_by.id,
          uploaded_at_after: transformDateFromDateRangeToStringFormatByIndex(this.searchData.uploadAt, 0),
          uploaded_at_before: transformDateFromDateRangeToStringFormatByIndex(this.searchData.uploadAt, 1)
        }
      },
      cleanFilters() {
        cleanFilters(this.searchData);
      },
      setQuickFilter() {
        this.attachmentsStorageGridOptions.api.setQuickFilter(this.searchData.quick_search);
      },
      async search() {
        const params = this.prepareSearchData();

        if (this.attachmentsStorageGridOptions.api) {
          this.attachmentsStorageGridOptions.api.showLoadingOverlay();
        }
        const {data, used_columns, grid_name} = await this.$http.get('generic/attachments_storage/', {params})
          .then(handlerResponse);
        this.attachmentsStorage = data;
        this.attachmentsStorageColumns = addColIdToColumns(used_columns);
        this.attachmentsStorageGridName = grid_name;
      },
      async saveFile(response) {
        const content = response.body.content;
        const uri = `data:${response.body['file_type']};base64,${content}`;
        const file = await fetch(uri);
        const blob = await file.blob();
        saveAs(blob, response.body['file_name']);
      },
      async downloadAttachment() {
        const selectedAttachments = getFilteredSelectedData(this.attachmentsStorageGridOptions);
        let notFoundedAttachments = [];
        for (let attachment of selectedAttachments) {
          try {
            const response = await this.$http.get(`generic/attachments_storage/${attachment.related_instance_type}/${attachment.id}`);
            await this.saveFile(response);
          } catch (e) {
            notFoundedAttachments.push(attachment.filename);
          }
        }
        if (notFoundedAttachments.length) {
          this.$notify(setNotifyData(this.$t('notifications.common.files_cannot_be_downloaded'), notFoundedAttachments.join(', ').toString(), 'error'));
        } else if (!notFoundedAttachments.length && selectedAttachments.length) {
          notificationsRenderer('Success', 'success');
        }
      }
    },
    beforeMount() {
      this.$store.cache.dispatch('generic/getInstancesTypes');
      this.$store.cache.dispatch('woodware/getUsers');
      if (checkSavedFilterData(this.storageKey)) {
        this.searchData = setFilters(this.storageKey);
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/pages/modules/general/documents-storage/DocumentsStorage.vue